import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { appointmentService } from './AppointmentsService';

const AppointmentForm = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete, 
  selectedSlot,
  doctors,
  currentUser 
}) => {
  const isEditMode = selectedSlot && selectedSlot.id;
  const [zoomMeetingLink, setZoomMeetingLink] = useState(null);
  const [zoomMeetingPassword, setZoomMeetingPassword] = useState(null);

 // Replace your createZoomMeeting function with this one
const createZoomMeeting = async () => {
  try {
    console.log('Starting Zoom meeting creation process');
    
    // Calculate meeting duration in minutes
    const durationMinutes = Math.ceil((formData.endTime - formData.startTime) / 60000);
    
    // Call backend API instead of Zoom API directly
    const response = await axios.post('http://localhost:5001/api/appointments/create-meeting', {
      title: formData.title || 'Medical Appointment',
      description: formData.description || 'Medical consultation appointment',
      startTime: formData.startTime.toISOString(),
      endTime: formData.endTime.toISOString(),
      durationMinutes: durationMinutes
    });
    
    const { meetingLink, meetingPassword } = response.data;
    console.log('Zoom meeting created successfully:', meetingLink);
    
    setZoomMeetingLink(meetingLink);
    setZoomMeetingPassword(meetingPassword);
    
    return {
      meetingLink: meetingLink,
      meetingPassword: meetingPassword
    };
  } catch (error) {
    console.error('Zoom Meeting Creation Error:', 
      error.response ? error.response.data : error.message);
    
    // User-friendly error handling
    if (error.response) {
      const status = error.response.status;
      if (status === 401 || status === 403) {
        setError('Authentication failed. Please contact support.');
      } else if (status === 429) {
        setError('Too many meeting requests. Please try again later.');
      } else {
        setError(`Failed to create Zoom meeting: ${error.response.data?.message || status}`);
      }
    } else {
      setError(`Network error creating Zoom meeting. Please check your connection.`);
    }
    
    return null;
  }
};

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    doctorId: '',
    patientId: currentUser?.role === 'patient' ? currentUser?._id : '',
    startTime: selectedSlot ? selectedSlot.start : new Date(),
    endTime: selectedSlot ? selectedSlot.end : new Date(Date.now() + 30 * 60 * 1000), // Default 30 min
  });
  
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
const [dateStr, setDateStr] = useState(format(formData.startTime, 'yyyy-MM-dd'));

// Add this effect to update dateStr when the date part of startTime changes
useEffect(() => {
  const newDateStr = format(formData.startTime, 'yyyy-MM-dd');
  // Only update if the date actually changed and this isn't just a time change
  if (newDateStr !== dateStr && !formData.timeOnly) {
    setDateStr(newDateStr);
  }
}, [formData.startTime]);

  // Helper to determine if button should be disabled
const isButtonDisabled = () => {
    // When editing, the button should be enabled
    if (isEditMode) return false;
    
    // For patients, need both doctor and time slot
    if (currentUser?.role === 'patient') {
      const needsDoctor = !formData.doctorId;
      const needsTimeSlot = !selectedTimeSlot;
      
      console.log('Button disabled check - needs doctor:', needsDoctor);
      console.log('Button disabled check - needs time slot:', needsTimeSlot);
      
      return loading || needsDoctor || needsTimeSlot;
    }
    
    // For doctors and admin, just need a patient
    return loading;
  };

  // Set up initial form data when editing an appointment
  useEffect(() => {
    if (selectedSlot && selectedSlot.resource) {
      // Handle existing appointment edit
      const { title, description, doctorId, patientId, startTime, endTime } = selectedSlot.resource;
      setFormData({
        id: selectedSlot.id,
        title: title || '',
        description: description || '',
        doctorId: doctorId || '',
        patientId: patientId || (currentUser?.role === 'patient' ? currentUser?.id : ''),
        startTime: startTime ? new Date(startTime) : selectedSlot.start,
        endTime: endTime ? new Date(endTime) : selectedSlot.end
      });
      
      // If we're editing, set the selected doctor
      if (doctorId) {
        const doctor = doctors.find(doc => doc._id === doctorId);
        if (doctor) {
          setSelectedDoctor(doctor);
        }
      }
    } else if (selectedSlot && selectedSlot.isAvailableSlot) {
      // Handle available slot selection
      setFormData(prev => ({
        ...prev,
        title: 'Appointment with Dr. ' + (selectedSlot.doctorInfo?.lastName || ''),
        doctorId: selectedSlot.doctorId || '',
        startTime: selectedSlot.start,
        endTime: selectedSlot.end
      }));
      
      // Set the selected doctor if we have the info
      if (selectedSlot.doctorId) {
        const doctor = doctors.find(doc => doc._id === selectedSlot.doctorId);
        if (doctor) {
          setSelectedDoctor(doctor);
        }
      }
      
      // Pre-select the time slot
      setSelectedTimeSlot({
        startTime: selectedSlot.start,
        endTime: selectedSlot.end
      });
    } else if (selectedSlot) {
      // Handle new manual slot selection
      setFormData(prev => ({
        ...prev,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end
      }));
    }
  }, [selectedSlot, currentUser, doctors]);

// Replace your useEffect for fetching available doctors
// Replace your useEffect for fetching available doctors with this corrected version

useEffect(() => {
    // Only get available doctors if user is a patient
    if (currentUser?.role !== 'patient' || !formData.startTime) return;
    
    // Compare only the date part, not the time
    const dateStr = format(formData.startTime, 'yyyy-MM-dd');
    
    const fetchDoctors = async () => {
      // Clear the timeOnly flag if it exists
      if (formData.timeOnly) {
        setFormData(prev => {
          const { timeOnly, ...rest } = prev;
          return rest;
        });
        return; // Skip fetching if this was just a time change
      }
      
      try {
        console.log(`Date changed to ${dateStr}, fetching available doctors`);
        setLoading(true);
        setError(null);
        
        // Call the actual fetchAvailableDoctors function
        const availableDocs = await appointmentService.getAvailableDoctorsForDate(dateStr);
        console.log('Available doctors:', availableDocs);
        
        if (!availableDocs || availableDocs.length === 0) {
          console.log('No doctors available for this date');
          setAvailableDoctors([]);  // Use empty array, not undefined
          return;
        }
        
        // For each doctor, fetch their available slots
        const doctorsWithSlots = await Promise.all(
          availableDocs.map(async doctor => {
            try {
              const slots = await appointmentService.getAvailableSlots(doctor.id, dateStr);
              
              if (slots && slots.length > 0) {
                return {
                  doctor: doctor,
                  availableSlots: slots
                };
              }
              return null;
            } catch (error) {
              console.error(`Error checking slots for doctor ${doctor._id}:`, error);
              return null;
            }
          })
        );
        
        // Filter out null values
        const filtered = doctorsWithSlots.filter(item => item !== null);
        console.log(`Found ${filtered.length} doctors with available slots`);
        
        setAvailableDoctors(filtered);
        
        // Reset selection if date changes
        if (dateStr !== format(formData.startTime, 'yyyy-MM-dd')) {
          setSelectedDoctor(null);
          setSelectedTimeSlot(null);
          setFormData(prev => ({
            ...prev,
            doctorId: ''
          }));
        }
        
      } catch (error) {
        console.error('Error fetching available doctors:', error);
        setError('Could not fetch available doctors. Please try again later.');
        setAvailableDoctors([]);  // Set empty array on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchDoctors();
    
  }, [dateStr, currentUser?.role]);

// Replace your handleDoctorSelect function with this version:

const handleDoctorSelect = (doctorWithSlots) => {
    console.log('Selected doctor with ID:', doctorWithSlots.doctor._id);
    
    // First set the doctor and slots
    setSelectedDoctor(doctorWithSlots);
    setAvailableSlots(doctorWithSlots.availableSlots);
    setSelectedTimeSlot(null); // Clear any previously selected time slot
    
    // IMPORTANT: Make a separate variable for doctorId to ensure it's properly captured
    const doctorId = doctorWithSlots.doctor._id;
    
    // Set form data using functional update to ensure we're working with the latest state
    setFormData(prevData => {
      const updated = {
        ...prevData,
        doctorId: doctorId, // Set doctor ID explicitly 
        title: `Appointment with Dr. ${doctorWithSlots.doctor.lastName || ''}`
      };
      console.log('Updated formData with doctor ID:', updated);
      return updated;
    });
    
    // Clear any previous errors
    setError(null);
  };

  // Handle standard form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

// First, modify the handleTimeSlotSelect function to avoid retriggering the doctor fetch:
const handleTimeSlotSelect = (slot) => {
    console.log('Selected time slot:', slot);
    
    if (!slot || !slot.startTime || !slot.endTime) {
      console.error('Invalid time slot data:', slot);
      return;
    }
    
    // Set the selected time slot
    setSelectedTimeSlot(slot);
    
    // Get the current date-only parts from formData.startTime
    const currentDate = new Date(formData.startTime);
    
    // Create new date objects but preserve the original date
    const newStartTime = new Date(slot.startTime);
    newStartTime.setFullYear(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    
    const newEndTime = new Date(slot.endTime);
    newEndTime.setFullYear(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    
    // Update form data with the new time slot
    setFormData(prev => ({
      ...prev,
      // Use these modified dates to avoid triggering a complete date change
      timeOnly: true, // Add a flag to indicate this is just a time change
      startTime: newStartTime,
      endTime: newEndTime
    }));
    
    console.log('Form data after slot selection:', {
      ...formData,
      startTime: newStartTime,
      endTime: newEndTime
    });
  };

  // Form submission
 // Replace your handleSubmit function with this:

// Form submission
// Form submission - fixed version
const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Starting form submission with data:', formData);
    
    // Basic validation
    if (!formData.title.trim()) {
      setError('Please enter an appointment title');
      return;
    }
    
    if (currentUser?.role === 'patient') {
      if (!formData.doctorId) {
        setError('Please select a doctor');
        return;
      }
      
      if (!selectedTimeSlot && !isEditMode) {
        setError('Please select an available time slot');
        return;
      }
    }
    
    // Show loading state
    setLoading(true);
    
    try {
      // Create a deep copy of the form data to avoid reference issues
       // Create a fresh object instead of using JSON.stringify/parse
    let finalFormData = {
        ...formData,
        // Ensure these fields are properly included
        doctorId: formData.doctorId,
        patientId: formData.patientId,
        startTime: formData.startTime,
        endTime: formData.endTime,
        title: formData.title,
        description: formData.description
      };
      
      // Create Zoom meeting if not in edit mode
      if (!isEditMode) {
        console.log('Creating Zoom meeting for new appointment...');
        const zoomMeeting = await createZoomMeeting();
        
        if (zoomMeeting && zoomMeeting.meetingLink) {
          console.log('Zoom meeting created successfully:', zoomMeeting);
          
          // IMPORTANT: Make sure we're actually updating finalFormData
          finalFormData.meetingLink = zoomMeeting.meetingLink;
          finalFormData.meetingPassword = zoomMeeting.meetingPassword;
          
          console.log('Updated finalFormData with meeting details:', finalFormData);
        } else {
          console.warn('Failed to create Zoom meeting, continuing without meeting link');
        }
      }
      
      console.log('Saving appointment with final data:', finalFormData);
      
      // Call onSave with the final form data (including Zoom details if created)
      await onSave(finalFormData);
      
      // Close form after successful save
      onClose();
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError('Failed to save appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  // Add this useEffect for debugging:

// Debug state changes
useEffect(() => {
    console.log('Form data changed:', formData);
    console.log('Selected doctor:', selectedDoctor);
    console.log('Selected time slot:', selectedTimeSlot);
  }, [formData, selectedDoctor, selectedTimeSlot]);

  const renderZoomMeetingSection = () => {
    if (!zoomMeetingLink) return null;

    return (
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <h4 className="font-medium text-blue-700">Zoom Meeting Details</h4>
        <div className="space-y-2">
          <p>
            <span className="font-semibold">Join URL:</span>{' '}
            <a 
              href={zoomMeetingLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:underline"
            >
              Join Zoom Meeting
            </a>
          </p>
          {zoomMeetingPassword && (
            <p>
              <span className="font-semibold">Meeting Password:</span>{' '}
              <span className="text-gray-700">{zoomMeetingPassword}</span>
            </p>
          )}
        </div>
      </div>
    );
  };


  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen">
        <div className="fixed inset-0 bg-black opacity-30" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg max-w-md w-full mx-auto p-6 shadow-xl">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {isEditMode ? 'Edit Appointment' : 'Schedule New Appointment'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={format(formData.startTime, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    const currentStart = new Date(formData.startTime);
                    const currentEnd = new Date(formData.endTime);
                    
                    newDate.setHours(currentStart.getHours(), currentStart.getMinutes());
                    
                    const newEnd = new Date(newDate);
                    newEnd.setHours(currentEnd.getHours(), currentEnd.getMinutes());
                    
                    setFormData({
                      ...formData,
                      startTime: newDate,
                      endTime: newEnd
                    });
                    
                    // Reset selections when date changes
                    setSelectedDoctor(null);
                    setSelectedTimeSlot(null);
                  }}
                  className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                  min={format(new Date(), 'yyyy-MM-dd')} // Prevent selecting past dates
                />
              </div>
              {renderZoomMeetingSection()}

              {currentUser?.role === 'patient' && (
                <>
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                      <p className="mt-2 text-sm text-gray-500">Looking for available doctors...</p>
                    </div>
                  ) : error ? (
                    <p className="text-red-500 text-sm">{error}</p>
                  ) : availableDoctors.length === 0 ? (
                    <p className="text-amber-500 text-sm p-3 bg-amber-50 rounded-md">
                      No doctors available on the selected date. Please try another date.
                    </p>
                  ) : (
                    <div className="mt-4">
                      <h3 className="font-medium text-gray-700 mb-2">Available Doctors</h3>
                      <div className="space-y-4 max-h-60 overflow-y-auto p-2 border rounded-md">
                        {availableDoctors.map((doctorWithSlots, index) => (
                          <div 
                            key={doctorWithSlots.doctor._id}
                            className={`p-3 rounded-md border cursor-pointer transition-colors ${
                              selectedDoctor?.doctor._id === doctorWithSlots.doctor._id
                                ? 'bg-blue-50 border-blue-300'
                                : 'hover:bg-gray-50 border-gray-200'
                            }`}
                            onClick={() => handleDoctorSelect(doctorWithSlots)}
                          >
                            <div className="font-medium">
                              Dr. {doctorWithSlots.doctor.firstName} {doctorWithSlots.doctor.lastName}
                            </div>
                            {doctorWithSlots.doctor.specialty && (
                              <div className="text-sm text-gray-600">{doctorWithSlots.doctor.specialty}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              {doctorWithSlots.availableSlots.length} available time slots
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDoctor && (
                    <div className="mt-4">
                      <h3 className="font-medium text-gray-700 mb-2">
                        Select Time Slot for Dr. {selectedDoctor.doctor.firstName} {selectedDoctor.doctor.lastName}
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border p-2 rounded-md">
                        {selectedDoctor.availableSlots.map((slot, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`px-3 py-2 text-sm rounded-md transition-all ${
                              selectedTimeSlot && 
                              format(new Date(selectedTimeSlot.startTime), 'HH:mm') === format(new Date(slot.startTime), 'HH:mm')
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                            onClick={() => handleTimeSlotSelect(slot)}
                          >
                            {format(new Date(slot.startTime), 'h:mm a')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* For doctors creating/editing appointments */}
              {currentUser?.role === 'doctor' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Patient
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    placeholder="Enter patient name"
                    className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is just a placeholder. You'd need to implement patient selection.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
                {error}
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <div>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => onDelete(formData.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                  >
                    Cancel Appointment
                  </button>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
               

<button
  type="submit"
  className={`px-4 py-2 rounded-md text-white font-medium ${
    isButtonDisabled() 
      ? 'bg-blue-400 cursor-not-allowed' 
      : 'bg-blue-600 hover:bg-blue-700'
  }`}
  // Remove the onClick handler - it may be interfering with form submission
  // Simply use the disabled attribute
  disabled={isButtonDisabled()}
>
  {isEditMode ? 'Update' : 'Schedule'}
</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentForm;